// src/views/Restaurant/Configuracion/AjustesPermisos/components/TablaPermisos/TablaPermisos.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  HiOutlineShieldCheck,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineUser,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlinePencilSquare,
  HiOutlinePlusCircle,
  HiOutlineTrash
} from 'react-icons/hi2';
import { FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  getUserPermissions,
  updateUserPermission,
  enablePermissionsByTypes,
  disablePermissionsByTypes
} from '../../../../../../crud/users_api/user_permissions';
import { getUsersByLocalId } from '../../../../../../crud/users_api/users';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import './TablaPermisos.scss';

// Types
interface Permission {
  name: string;
  permission_id: number;
  description: string;
  category: string;
  subcategory: string | null;
  permission_type: string | null;
  enabled: boolean;
  last_modified: string | null;
  modified_by_user_id: number | null;
  modified_by_user_name?: string;
}

interface User {
  user_id: number;
  username: string;
  last_name: string;
}

interface GroupedPermissions {
  [category: string]: {
    [subcategory: string]: Permission[];
  };
}

// Permission type configuration - using orange palette
const PERMISSION_TYPES = [
  { value: 'read', label: 'Leer', icon: HiOutlineEye, color: '#FC7556' },
  { value: 'create', label: 'Crear', icon: HiOutlinePlusCircle, color: '#e85d3a' },
  { value: 'update', label: 'Actualizar', icon: HiOutlinePencilSquare, color: '#d14a28' },
  { value: 'delete', label: 'Eliminar', icon: HiOutlineTrash, color: '#b83d1e' }
];

const TablaPermisos: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState<string | null>(null);

  // Load users on mount
  useEffect(() => {
    const user = localStorage.getItem('user');
    const localId = user ? JSON.parse(user).local_id : 0;

    getUsersByLocalId(localId)
      .then((response) => {
        setUsers(response.data);
        setLoading(false);
        if (response.data.length > 0) {
          const firstId = response.data[0].user_id;
          setSelectedUserId(firstId);
          loadUserPermissions(firstId);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        toast.error('Error al cargar usuarios');
      });
  }, []);

  // Load permissions for a user
  const loadUserPermissions = async (userId: number) => {
    setLoadingPermissions(true);
    try {
      const response = await getUserPermissions(userId);
      const perms: Permission[] = Object.values(response.data);
      setPermissions(perms);
      
      // Expand all categories by default
      const categories = new Set<string>(perms.map(p => p.category));
      setExpandedCategories(categories);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar permisos');
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Handle user change
  const handleUserChange = (userId: number) => {
    setSelectedUserId(userId);
    setSearchTerm('');
    setTypeFilter('all');
    loadUserPermissions(userId);
  };

  // Group and filter permissions
  const groupedPermissions = useMemo(() => {
    let filtered = permissions.filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      p.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.subcategory?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Apply type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'none') {
        filtered = filtered.filter(p => !p.permission_type);
      } else {
        filtered = filtered.filter(p => p.permission_type === typeFilter);
      }
    }

    // Group by category, then subcategory
    const grouped: GroupedPermissions = {};
    
    filtered.forEach(permission => {
      const category = permission.category || 'Sin categoría';
      const subcategory = permission.subcategory || 'General';
      
      if (!grouped[category]) {
        grouped[category] = {};
      }
      if (!grouped[category][subcategory]) {
        grouped[category][subcategory] = [];
      }
      grouped[category][subcategory].push(permission);
    });

    // Sort subcategories and permissions within each
    Object.keys(grouped).forEach(category => {
      const sortedSubcats: { [key: string]: Permission[] } = {};
      Object.keys(grouped[category]).sort().forEach(sub => {
        sortedSubcats[sub] = grouped[category][sub].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
      });
      grouped[category] = sortedSubcats;
    });

    return grouped;
  }, [permissions, searchTerm, typeFilter]);

  // Toggle permission
  const handleTogglePermission = async (permissionId: number, enabled: boolean) => {
    if (!selectedUserId) return;

    // Optimistic update
    setPermissions(prev =>
      prev.map(p =>
        p.permission_id === permissionId ? { ...p, enabled } : p
      )
    );

    try {
      await updateUserPermission(selectedUserId, permissionId, enabled);
      toast.success('Permiso actualizado');
    } catch (error) {
      // Revert on error
      setPermissions(prev =>
        prev.map(p =>
          p.permission_id === permissionId ? { ...p, enabled: !enabled } : p
        )
      );
      toast.error('Error al actualizar permiso');
    }
  };

  // Bulk enable permissions by type
  const handleBulkEnable = async (type: string) => {
    if (!selectedUserId) return;
    
    setBulkLoading(`enable-${type}`);
    try {
      await enablePermissionsByTypes(selectedUserId, [type]);
      // Reload permissions
      await loadUserPermissions(selectedUserId);
      const typeLabel = PERMISSION_TYPES.find(t => t.value === type)?.label || type;
      toast.success(`Permisos de "${typeLabel}" habilitados`);
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Bulk disable permissions by type
  const handleBulkDisable = async (type: string) => {
    if (!selectedUserId) return;
    
    setBulkLoading(`disable-${type}`);
    try {
      await disablePermissionsByTypes(selectedUserId, [type]);
      await loadUserPermissions(selectedUserId);
      const typeLabel = PERMISSION_TYPES.find(t => t.value === type)?.label || type;
      toast.success(`Permisos de "${typeLabel}" deshabilitados`);
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Enable all permissions
  const handleEnableAll = async () => {
    if (!selectedUserId) return;
    
    setBulkLoading('enable-all');
    try {
      await enablePermissionsByTypes(selectedUserId, ['read', 'create', 'update', 'delete']);
      await loadUserPermissions(selectedUserId);
      toast.success('Todos los permisos habilitados');
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Disable all permissions
  const handleDisableAll = async () => {
    if (!selectedUserId) return;
    
    setBulkLoading('disable-all');
    try {
      await disablePermissionsByTypes(selectedUserId, ['read', 'create', 'update', 'delete']);
      await loadUserPermissions(selectedUserId);
      toast.success('Todos los permisos deshabilitados');
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Enable all permissions in a category
  const handleEnableCategory = async (category: string) => {
    if (!selectedUserId) return;
    
    setBulkLoading(`enable-cat-${category}`);
    try {
      const categoryPerms = Object.values(groupedPermissions[category] || {}).flat();
      await Promise.all(
        categoryPerms.filter(p => !p.enabled).map(p => 
          updateUserPermission(selectedUserId, p.permission_id, true)
        )
      );
      await loadUserPermissions(selectedUserId);
      toast.success(`Permisos de "${category}" habilitados`);
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Disable all permissions in a category
  const handleDisableCategory = async (category: string) => {
    if (!selectedUserId) return;
    
    setBulkLoading(`disable-cat-${category}`);
    try {
      const categoryPerms = Object.values(groupedPermissions[category] || {}).flat();
      await Promise.all(
        categoryPerms.filter(p => p.enabled).map(p => 
          updateUserPermission(selectedUserId, p.permission_id, false)
        )
      );
      await loadUserPermissions(selectedUserId);
      toast.success(`Permisos de "${category}" deshabilitados`);
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Enable all permissions in a subcategory
  const handleEnableSubcategory = async (category: string, subcategory: string) => {
    if (!selectedUserId) return;
    
    const subKey = `${category}-${subcategory}`;
    setBulkLoading(`enable-sub-${subKey}`);
    try {
      const subPerms = groupedPermissions[category]?.[subcategory] || [];
      await Promise.all(
        subPerms.filter(p => !p.enabled).map(p => 
          updateUserPermission(selectedUserId, p.permission_id, true)
        )
      );
      await loadUserPermissions(selectedUserId);
      toast.success(`Permisos de "${subcategory}" habilitados`);
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Disable all permissions in a subcategory
  const handleDisableSubcategory = async (category: string, subcategory: string) => {
    if (!selectedUserId) return;
    
    const subKey = `${category}-${subcategory}`;
    setBulkLoading(`disable-sub-${subKey}`);
    try {
      const subPerms = groupedPermissions[category]?.[subcategory] || [];
      await Promise.all(
        subPerms.filter(p => p.enabled).map(p => 
          updateUserPermission(selectedUserId, p.permission_id, false)
        )
      );
      await loadUserPermissions(selectedUserId);
      toast.success(`Permisos de "${subcategory}" deshabilitados`);
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Toggle subcategory expansion
  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Get category permission count
  const getCategoryCount = (category: string) => {
    return Object.values(groupedPermissions[category] || {}).reduce(
      (sum, perms) => sum + perms.length, 0
    );
  };

  // Get enabled count for category
  const getCategoryEnabledCount = (category: string) => {
    return Object.values(groupedPermissions[category] || {}).reduce(
      (sum, perms) => sum + perms.filter(p => p.enabled).length, 0
    );
  };

  // Get permission type badge
  const getTypeBadge = (type: string | null) => {
    if (!type) return null;
    const config = PERMISSION_TYPES.find(t => t.value === type);
    if (!config) return null;
    const Icon = config.icon;
    return (
      <span className={`type-badge type-${type}`} style={{ '--type-color': config.color } as React.CSSProperties}>
        <Icon size={12} />
        <span>{config.label}</span>
      </span>
    );
  };

  // Get selected user name
  const selectedUser = users.find(u => u.user_id === selectedUserId);
  const selectedUserName = selectedUser ? `${selectedUser.username} ${selectedUser.last_name}` : '';

  if (loading) {
    return (
      <div className="permisos-loading">
        <MiniLoadingSpinner />
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="permisos-manager">
      {/* User Selector */}
      <div className="user-selector">
        <div className="selector-label">
          <HiOutlineUser size={20} />
          <span>Usuario:</span>
        </div>
        <Dropdown className="user-dropdown-container">
          <Dropdown.Toggle 
            variant="none" 
            className="user-dropdown"
            id="user-selector-dropdown"
          >
            {selectedUser ? `${selectedUser.username} ${selectedUser.last_name}` : 'Seleccione un usuario'}
          </Dropdown.Toggle>
          <Dropdown.Menu className="user-dropdown-menu">
            {users.map(user => (
              <Dropdown.Item
                key={user.user_id}
                onClick={() => handleUserChange(user.user_id)}
                active={selectedUserId === user.user_id}
              >
                <div className="user-item">
                  <HiOutlineUser size={16} />
                  <span className="user-name">{user.username} {user.last_name}</span>
                </div>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Bulk Actions */}
      {selectedUserId && (
        <div className="bulk-actions">
          <div className="bulk-section">
            <span className="bulk-label">Acciones por tipo:</span>
            <div className="type-actions">
              {PERMISSION_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <div key={type.value} className="type-action-group">
                    <span className="type-name" style={{ color: type.color }}>
                      <Icon size={16} />
                      {type.label}
                    </span>
                    <button
                      className="bulk-btn enable"
                      onClick={() => handleBulkEnable(type.value)}
                      disabled={bulkLoading !== null}
                      title={`Habilitar todos los permisos de ${type.label}`}
                    >
                      {bulkLoading === `enable-${type.value}` ? (
                        <MiniLoadingSpinner />
                      ) : (
                        <HiOutlineCheck size={14} />
                      )}
                    </button>
                    <button
                      className="bulk-btn disable"
                      onClick={() => handleBulkDisable(type.value)}
                      disabled={bulkLoading !== null}
                      title={`Deshabilitar todos los permisos de ${type.label}`}
                    >
                      {bulkLoading === `disable-${type.value}` ? (
                        <MiniLoadingSpinner />
                      ) : (
                        <HiOutlineXMark size={14} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bulk-all">
            <button
              className="all-btn enable"
              onClick={handleEnableAll}
              disabled={bulkLoading !== null}
            >
              {bulkLoading === 'enable-all' ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <HiOutlineCheck size={16} />
              )}
              {bulkLoading !== 'enable-all' && 'Habilitar Todos'}
            </button>
            <button
              className="all-btn disable"
              onClick={handleDisableAll}
              disabled={bulkLoading !== null}
            >
              {bulkLoading === 'disable-all' ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <HiOutlineXMark size={16} />
              )}
              {bulkLoading !== 'disable-all' && 'Deshabilitar Todos'}
            </button>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      {selectedUserId && (
        <div className="permisos-filters">
          <div className="permisos-search">
            <FiSearch size={18} />
            <input
              type="text"
              placeholder="Buscar permisos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="type-filter">
            <Dropdown className="type-dropdown-container">
              <Dropdown.Toggle
                variant="none"
                className="type-dropdown"
                id="type-filter-dropdown"
              >
                {typeFilter === 'all' ? 'Todos los tipos' : typeFilter === 'none' ? 'Sin tipo' : PERMISSION_TYPES.find(t => t.value === typeFilter)?.label || 'Filtrar por tipo'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="type-dropdown-menu">
                <Dropdown.Item
                  onClick={() => setTypeFilter('all')}
                  active={typeFilter === 'all'}
                >
                  Todos los tipos
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setTypeFilter('none')}
                  active={typeFilter === 'none'}
                >
                  Sin tipo
                </Dropdown.Item>
                {PERMISSION_TYPES.map(type => (
                  <Dropdown.Item
                    key={type.value}
                    onClick={() => setTypeFilter(type.value)}
                    active={typeFilter === type.value}
                  >
                    {type.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      )}

      {/* Permissions List */}
      {loadingPermissions ? (
        <div className="permisos-loading">
          <MiniLoadingSpinner />
          <p>Cargando permisos de {selectedUserName}...</p>
        </div>
      ) : selectedUserId && Object.keys(groupedPermissions).length > 0 ? (
        <div className="permisos-list">
          {Object.entries(groupedPermissions).map(([category, subcats]) => {
            const isExpanded = expandedCategories.has(category);
            const totalCount = getCategoryCount(category);
            const enabledCount = getCategoryEnabledCount(category);
            
            return (
              <div key={category} className="category-group">
                <div className="category-header">
                  <div 
                    className="category-header-main"
                    onClick={() => toggleCategory(category)}
                  >
                    {isExpanded ? (
                      <HiOutlineChevronDown size={20} />
                    ) : (
                      <HiOutlineChevronRight size={20} />
                    )}
                    <span className="category-name">{category}</span>
                    <span className="category-stats">
                      <span className="enabled-count">{enabledCount}</span>
                      <span className="separator">/</span>
                      <span className="total-count">{totalCount}</span>
                    </span>
                  </div>
                  <div className="category-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="category-bulk-btn enable"
                      onClick={() => handleEnableCategory(category)}
                      disabled={bulkLoading !== null}
                      title="Habilitar todos los permisos de esta categoría"
                    >
                      {bulkLoading === `enable-cat-${category}` ? (
                        <MiniLoadingSpinner />
                      ) : (
                        <HiOutlineCheck size={14} />
                      )}
                    </button>
                    <button
                      className="category-bulk-btn disable"
                      onClick={() => handleDisableCategory(category)}
                      disabled={bulkLoading !== null}
                      title="Deshabilitar todos los permisos de esta categoría"
                    >
                      {bulkLoading === `disable-cat-${category}` ? (
                        <MiniLoadingSpinner />
                      ) : (
                        <HiOutlineXMark size={14} />
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="category-content">
                    {Object.entries(subcats).map(([subcategory, perms]) => {
                      const subKey = `${category}-${subcategory}`;
                      const isSubExpanded = expandedSubcategories.has(subKey) || subcategory === 'General';
                      const subEnabledCount = perms.filter(p => p.enabled).length;
                      
                      return (
                        <div key={subKey} className="subcategory-group">
                          <div className="subcategory-header">
                            <div 
                              className="subcategory-header-main"
                              onClick={() => toggleSubcategory(subKey)}
                            >
                              {isSubExpanded ? (
                                <HiOutlineChevronDown size={16} />
                              ) : (
                                <HiOutlineChevronRight size={16} />
                              )}
                              <span className="subcategory-name">{subcategory}</span>
                              <span className="subcategory-stats">
                                {subEnabledCount}/{perms.length}
                              </span>
                            </div>
                            <div className="subcategory-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="subcategory-bulk-btn enable"
                                onClick={() => handleEnableSubcategory(category, subcategory)}
                                disabled={bulkLoading !== null}
                                title="Habilitar todos los permisos de esta subcategoría"
                              >
                                {bulkLoading === `enable-sub-${subKey}` ? (
                                  <MiniLoadingSpinner />
                                ) : (
                                  <HiOutlineCheck size={12} />
                                )}
                              </button>
                              <button
                                className="subcategory-bulk-btn disable"
                                onClick={() => handleDisableSubcategory(category, subcategory)}
                                disabled={bulkLoading !== null}
                                title="Deshabilitar todos los permisos de esta subcategoría"
                              >
                                {bulkLoading === `disable-sub-${subKey}` ? (
                                  <MiniLoadingSpinner />
                                ) : (
                                  <HiOutlineXMark size={12} />
                                )}
                              </button>
                            </div>
                          </div>

                          {isSubExpanded && (
                            <div className="permissions-items">
                              {perms.map(permission => (
                                <div
                                  key={permission.permission_id}
                                  className={`permission-item ${permission.enabled ? 'enabled' : 'disabled'}`}
                                >
                                  <div className="permission-info">
                                    <div className="permission-header">
                                      {getTypeBadge(permission.permission_type)}
                                    </div>
                                    <span className="permission-name">{permission.name}</span>
                                    {permission.description && (
                                      <span className="permission-description">{permission.description}</span>
                                    )}
                                  </div>
                                  <label className="toggle-switch">
                                    <input
                                      type="checkbox"
                                      checked={permission.enabled}
                                      onChange={() => handleTogglePermission(
                                        permission.permission_id,
                                        !permission.enabled
                                      )}
                                    />
                                    <span className="slider"></span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : selectedUserId ? (
        <div className="no-permissions">
          <HiOutlineShieldCheck size={48} />
          <p>No se encontraron permisos</p>
        </div>
      ) : null}
    </div>
  );
};

export default TablaPermisos;
